import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supaFetch } from '../lib/supabase.js';
import { queryKeys } from '../lib/queryKeys.js';

// ─── WHO DO WE ACTUALLY CONTACT ABOUT THIS ORDER? ─────────────────────────────
// orders.contact_name/phone/email are an immutable snapshot of who PLACED the
// order — deliberately never rewritten, so history survives staff turnover at
// the customer. That makes them the wrong target for fulfillment comms once
// that person leaves.
//
// Resolution order:
//   1. orders.fulfillment_contact_id — this order was explicitly re-pointed
//   2. the account's current active PRIMARY contact (via orders.retailer_id)
//   3. the order's frozen snapshot (guest/legacy orders with no account)
//
// One query for every contact (~1-3 rows per account, tens overall) rather
// than one per order row, and it shares the 'retailer-contacts' key prefix so
// promoting or deactivating someone in Applications refreshes this too.
export function useAllRetailerContacts() {
  return useQuery({
    queryKey: queryKeys.admin.retailerContactsAll(),
    queryFn: () => supaFetch('GET', 'retailer_contacts', null,
      '?select=*&order=active.desc,is_primary.desc,created_at.asc'),
    select: (result) => {
      const rows = result.data ?? [];
      const byId = new Map();
      const byAccount = new Map();
      for (const c of rows) {
        byId.set(c.id, c);
        if (!byAccount.has(c.retailer_application_id)) byAccount.set(c.retailer_application_id, []);
        byAccount.get(c.retailer_application_id).push(c);
      }
      return { byId, byAccount };
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useOrderRecipients() {
  const { data } = useAllRetailerContacts();

  // Active contacts for an account, for the "send fulfillment comms to" picker.
  const contactsForAccount = (accountId) =>
    (data?.byAccount.get(accountId) || []).filter(c => c.active);

  const primaryFor = (accountId) =>
    contactsForAccount(accountId).find(c => c.is_primary) || null;

  const recipientFor = (order) => {
    const pinned  = order?.fulfillment_contact_id ? data?.byId.get(order.fulfillment_contact_id) : null;
    const primary = order?.retailer_id ? primaryFor(order.retailer_id) : null;
    const c = pinned || primary;

    const placedBy = {
      name:  order?.contact_name || '',
      email: order?.email || '',
      phone: order?.whatsapp || order?.phone || '',
    };
    if (!c) return { ...placedBy, source: 'order', contact: null, differsFromPlacedBy: false };

    const resolved = {
      name:  c.name || placedBy.name,
      // Fall back per-field: a contact record may legitimately have no email.
      email: c.email || placedBy.email,
      phone: c.whatsapp || c.phone || placedBy.phone,
    };
    return {
      ...resolved,
      source: pinned ? 'fulfillment' : 'primary',
      contact: c,
      differsFromPlacedBy:
        resolved.email !== placedBy.email ||
        resolved.phone !== placedBy.phone ||
        resolved.name  !== placedBy.name,
    };
  };

  return { recipientFor, contactsForAccount, ready: !!data };
}

// Re-point an in-flight order at a live person WITHOUT rewriting who placed it.
export function useSetFulfillmentContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, contactId }) =>
      supaFetch('PATCH', 'orders', { fulfillment_contact_id: contactId || null },
        `?id=eq.${encodeURIComponent(orderId)}`, { Prefer: 'return=minimal' }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.admin.ordersRoot() }),
  });
}
